import { isValid } from "luhn-js";

interface BarcodesRange {
    start: string;
    end: string;
    type: string;
    size: number;
}

interface BarcodeType {
    name: string;
    size: number|null;
}

const barcodeTypes: BarcodeType[] = [
    { name:"EAN-8", size: 7 },
    { name:"CODE-128", size: null },
    { name:"LUHN", size: null },
]

const isValidBarcodeInRanges = function(barcodeString: string, validRanges: BarcodesRange[]): boolean
{
    return validRanges.some(function (range) {
        const barcodeType = barcodeTypes.find((type) => type.name === range.type);

        if (!barcodeType) {
            return false;
        }
        if (!range.start || !range.end) {
            return false;
        }

        if (range.type === 'EAN-8') {
            return isValidEan8InRange(barcodeString, range) &&
                isValidEan8Checksum(barcodeString);
        } else if (range.type === 'CODE-128') {
            return isValidCode128InRange(barcodeString, range)
        } else if (range.type === 'LUHN' && (parseInt(range.size) + 1) === barcodeString.length) {
            return isValid(barcodeString)
        }
        return false;
    });
}

const isValidLuhnInRange = function(barcodeString: string, range: BarcodesRange): boolean
{
    return range.type === 'LUHN'
        && barcodeString.length === parseInt(range.size)
        && barcodeString >= range.start
        && barcodeString <= range.end
}

const isValidEan8InRange = function(barcodeString: string, range: BarcodesRange): boolean
{
    return range.type === 'EAN-8'
        && barcodeString.length >= 7
        && barcodeString.substring(0, 7) >= range.start
        && barcodeString.substring(0, 7) <= range.end
}

const isValidEan8Checksum = function(barcodeString: string)
{
    let lastDigit = Number(barcodeString.substring(7));
    if (isNaN(lastDigit)) {
        return false; // not a valid upc/ean
    }
    return barcodeCheckSum(barcodeString.substring(0, 7)) === lastDigit;
}

const isValidCode128InRange = function(barcodeString: string, range: BarcodesRange)
{
    const barcodeSize = parseInt(range.size);
    if (!barcodeSize || (barcodeString.length !== barcodeSize)) {
        return false;
    }

    // check if barcode if between star and end
    if (barcodeString.substring(0, barcodeSize) < range.start ||
        barcodeString.substring(0, barcodeSize) > range.end) {
        return false;
    }
    return true
}

const barcodeCheckSum = function(barcode: string): number
{
    let arr = barcode.substring(0,barcode.length).split("").reverse();
    let oddTotal = 0, evenTotal = 0;

    for (let i = 0; i < arr.length; i++) {
        if (isNaN(arr[i])) {
            return null
        } // can't be a valid upc/ean we're checking for

        if (i % 2 === 0) {
            oddTotal += Number(arr[i]) * 3;
        } else {
            evenTotal += Number(arr[i]);
        }
    }
    return (10 - ((evenTotal + oddTotal) % 10)) % 10;
}

const generateInputMask = function(range: BarcodesRange, escape: boolean = false) {
    let startStr = range.start.toString();
    let endStr = range.end.toString();

    let fixedPart = '';
    for (let i = 0; i < startStr.length; i++) {
        if (startStr[i] === endStr[i]) {
            fixedPart += startStr[i];
        } else {
            break;
        }
    }
    var length = startStr.length;

    let barcodeType = barcodeTypes.find((barcodeType) => barcodeType.name === range.type);
    if (barcodeType) {
        if (['EAN-8'].includes(barcodeType.name)) {
            length = (barcodeType.size ?? 0) + 1;
        } else if (['LUHN'].includes(barcodeType.name)) {
            length = parseInt(range.size) + 1;
        }
    }

    let variablePartLength = length - fixedPart.length;
    fixedPart = fixedPart.split('').map((char) => { return (escape ? '!' : '') + char }).join('');

    let mask = fixedPart + '#'.repeat(variablePartLength);
    return mask;
}

const getMasksFromConfig = function(ranges: BarcodesRange[]): (value: string) => string|null
{
    return (value: string) => {

        const rangeMatch = ranges.filter(function(range) {
            const rangeStart = generateInputMask(range).replaceAll('#', '');
            return rangeStart.startsWith(value.substring(0, rangeStart.length));
        })


        const rangeMasks: string[] = (rangeMatch.length > 0 ? rangeMatch : ranges )
          .map(function(range) {
              return generateInputMask(range, true);
          });

        if (rangeMasks.length === 0) return null;
        let mask: string = rangeMasks[0];

        for (let i = 1; i < rangeMasks.length; i++) {
            let j = 0;

            // Trouver la longueur du préfixe commun entre `prefixe` et la chaîne actuelle
            while (j < mask.length && j < rangeMasks[i].length && mask[j] === rangeMasks[i][j]) {
                j++;
            }

            // Réduire le préfixe à la partie commune
            mask = mask.slice(0, j);

            // Si le préfixe devient vide, il n'y a pas de préfixe commun
            if (mask === "") break;
        }

        if (mask === '') {
            return null;
        }
        // remove last character if it's a !
        if (mask[mask.length - 1] === '!') {
            mask = mask.slice(0, -1);
        }
        return mask;
    }
}

const getPrefixesFromConfig = function(ranges: BarcodesRange[]) {
    const allNumbers = ranges.flatMap(interval => [
        interval.start,
        interval.end
    ]) as string[];

    // Trouver le préfixe commun de deux chaînes
    function commonPrefix(str1: string, str2: string) {
        let prefix = "";
        const minLength = Math.min(str1.length, str2.length);
        for (let i = 0; i < minLength; i++) {
            if (str1[i] !== str2[i]) break;
            prefix += str1[i];
        }
        return prefix;
    }

    // Trouver le préfixe commun pour toutes les chaînes
    return allNumbers.length > 0
        ? allNumbers.reduce((prefix, number) => commonPrefix(prefix, number))
        : '';
}

export {
    isValidBarcodeInRanges,
    isValidEan8InRange,
    isValidCode128InRange,
    isValidLuhnInRange,
    barcodeCheckSum,
    generateInputMask,
    getMasksFromConfig,
    getPrefixesFromConfig,
}

export type {
    BarcodesRange
}